import { useState, useEffect, useCallback, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import {
  campaigns as api,
  type SendLogItem,
  type SendLogSummary,
  type Campaign,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowLeft, Search, Download, ChevronLeft, ChevronRight,
  Mail, Eye, MousePointerClick, AlertTriangle, UserMinus,
  Clock, CheckCircle2, XCircle, ShieldAlert, ChevronDown,
  ChevronUp, ExternalLink, Copy, X,
} from "lucide-react";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "sent", label: "Sent" },
  { key: "failed", label: "Failed" },
  { key: "opened", label: "Opened" },
  { key: "clicked", label: "Clicked" },
  { key: "bounced", label: "Bounced" },
  { key: "unsubscribed", label: "Unsubscribed" },
  { key: "complained", label: "Complained" },
  { key: "pending", label: "Pending" },
] as const;

const STATUS_ICON: Record<string, React.ElementType> = {
  sent: CheckCircle2,
  failed: XCircle,
  bounced: AlertTriangle,
  unsubscribed: UserMinus,
  complained: ShieldAlert,
  pending: Clock,
};

const STATUS_COLOR: Record<string, string> = {
  sent: "text-emerald-600",
  failed: "text-red-500",
  bounced: "text-red-500",
  unsubscribed: "text-amber-600",
  complained: "text-red-600",
  pending: "text-amber-500",
};

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  accent,
  active,
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  accent: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white rounded-2xl border p-4 transition-all hover:shadow-sm ${
        active ? "border-coral ring-1 ring-coral/20" : "border-black/[0.06]"
      }`}
    >
      <div className={`inline-flex p-2 rounded-xl mb-2 ${accent}`}>
        <Icon size={16} />
      </div>
      <p className="text-xl font-bold text-ink leading-none">{value.toLocaleString()}</p>
      <p className="text-[11px] font-semibold text-slate uppercase tracking-wider mt-1">
        {label}
      </p>
    </button>
  );
}

function EventTimeline({ events }: { events: SendLogItem["events"] }) {
  const EVENT_LABELS: Record<string, { label: string; color: string }> = {
    sent: { label: "Delivered", color: "bg-emerald-500" },
    failed: { label: "Failed", color: "bg-red-500" },
    opened: { label: "Opened", color: "bg-blue-500" },
    clicked: { label: "Clicked", color: "bg-purple-500" },
    bounced: { label: "Bounced", color: "bg-red-500" },
    unsubscribed: { label: "Unsubscribed", color: "bg-amber-500" },
    complained: { label: "Complained", color: "bg-red-600" },
    queued: { label: "Queued", color: "bg-amber-400" },
  };

  return (
    <div className="pl-4 border-l-2 border-black/[0.06] space-y-3 py-1">
      {events.map((ev, i) => {
        const cfg = EVENT_LABELS[ev.type] || {
          label: ev.type,
          color: "bg-gray-400",
        };
        return (
          <div key={i} className="relative flex items-start gap-3">
            <div
              className={`absolute -left-[calc(1rem+5px)] top-1.5 w-2 h-2 rounded-full ${cfg.color}`}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">{cfg.label}</p>
              <p className="text-xs text-slate">{formatDate(ev.timestamp)}</p>
              {ev.metadata?.error && (
                <p className="text-xs text-red-500 mt-0.5 break-all">
                  {ev.metadata.error}
                </p>
              )}
              {ev.metadata?.url && (
                <p className="text-xs text-teal mt-0.5 truncate max-w-xs">
                  {ev.metadata.url}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExpandedRow({ item }: { item: SendLogItem }) {
  const [copied, setCopied] = useState(false);
  const [showBody, setShowBody] = useState(false);

  const copyMessageId = () => {
    if (item.message_id) {
      navigator.clipboard.writeText(item.message_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <tr>
      <td colSpan={7} className="px-5 pb-5 pt-0">
        <div className="bg-paper rounded-xl border border-black/[0.04] p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Details */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-ink">Send Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate">Subject</span>
                  <span className="text-ink font-medium text-right max-w-[260px] truncate">
                    {item.subject}
                  </span>
                </div>
                {item.variant_index !== null && item.variant_index > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate">A/B Variant</span>
                    <span className="text-ink">
                      Variant {String.fromCharCode(65 + item.variant_index)}
                    </span>
                  </div>
                )}
                {item.message_id && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-slate">Message ID</span>
                    <button
                      onClick={copyMessageId}
                      className="flex items-center gap-1 text-xs text-teal hover:underline"
                    >
                      <span className="truncate max-w-[180px] font-mono text-[11px]">
                        {item.message_id}
                      </span>
                      <Copy size={12} />
                      {copied && (
                        <span className="text-emerald-600 text-[10px]">
                          Copied
                        </span>
                      )}
                    </button>
                  </div>
                )}
                {item.error && (
                  <div>
                    <span className="text-slate">Error</span>
                    <p className="text-red-500 text-xs mt-0.5 break-all bg-red-50 rounded-lg p-2">
                      {item.error}
                    </p>
                  </div>
                )}
                {item.click_url && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-slate">Clicked URL</span>
                    <a
                      href={item.click_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-teal hover:underline truncate max-w-[200px]"
                    >
                      {item.click_url}
                      <ExternalLink size={11} />
                    </a>
                  </div>
                )}
                {item.html_content && (
                  <button
                    onClick={() => setShowBody(!showBody)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal hover:text-teal-dark mt-1"
                  >
                    <Eye size={13} />
                    {showBody ? "Hide Email Body" : "View Email Body"}
                  </button>
                )}
              </div>
            </div>

            {/* Right: Event Timeline */}
            <div>
              <h4 className="text-sm font-semibold text-ink mb-3">
                Event Timeline
              </h4>
              <EventTimeline events={item.events} />
            </div>
          </div>

          {/* Email Body Preview */}
          {showBody && item.html_content && (
            <div className="border-t border-black/[0.06] pt-4">
              <h4 className="text-sm font-semibold text-ink mb-3">Email Body</h4>
              <div className="bg-white rounded-xl border border-black/[0.06] overflow-hidden">
                <iframe
                  srcDoc={item.html_content}
                  className="w-full border-0"
                  style={{ minHeight: "400px" }}
                  sandbox="allow-same-origin"
                  title="Email content"
                  onLoad={(e) => {
                    const iframe = e.target as HTMLIFrameElement;
                    if (iframe.contentDocument) {
                      iframe.style.height = Math.min(
                        iframe.contentDocument.body.scrollHeight + 40,
                        800
                      ) + "px";
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function CampaignSendLogPage() {
  const { id } = useParams<{ id: string }>();
  const [items, setItems] = useState<SendLogItem[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [summary, setSummary] = useState<SendLogSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const limit = 50;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.sendLog(id, {
        page,
        limit,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setItems(res.items);
      setCampaign(res.campaign);
      setSummary(res.summary || null);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [id, page, limit, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
    setExpandedRow(null);
  }, [statusFilter, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const exportCsv = () => {
    if (!items.length) return;
    const esc = (v: string) => {
      if (v.includes(",") || v.includes('"') || v.includes("\n")) {
        return `"${v.replace(/"/g, '""')}"`;
      }
      return v;
    };
    const headers = [
      "Email",
      "Name",
      "Status",
      "Subject",
      "Sent At",
      "Opened",
      "Opened At",
      "Clicked",
      "Clicked At",
      "Click URL",
      "Bounced",
      "Unsubscribed",
      "Error",
      "Message ID",
    ];
    const rows = items.map((i) => [
      esc(i.email),
      esc(i.name || ""),
      esc(i.status),
      esc(i.subject || ""),
      esc(i.sent_at || ""),
      i.opened ? "Yes" : "No",
      esc(i.opened_at || ""),
      i.clicked ? "Yes" : "No",
      esc(i.clicked_at || ""),
      esc(i.click_url || ""),
      i.bounced ? "Yes" : "No",
      i.unsubscribed ? "Yes" : "No",
      esc(i.error || ""),
      esc(i.message_id || ""),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `send-log-${campaign?.name || id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={id ? `/campaigns/${id}` : "/campaigns"}
            className="inline-flex items-center gap-1.5 text-sm text-slate hover:text-ink transition mb-3"
          >
            <ArrowLeft size={15} />
            Back to campaign
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-ink">
                Send Log
              </h1>
              {campaign && (
                <p className="text-slate mt-1 text-sm">
                  {campaign.name}
                  {campaign.status && (
                    <span className="ml-2">
                      <StatusBadge status={campaign.status} />
                    </span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={exportCsv}
              disabled={items.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-black/[0.08] text-sm font-medium text-ink hover:bg-sand/50 transition disabled:opacity-40"
            >
              <Download size={15} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <SummaryCard
              label="Sent"
              value={summary.sent}
              icon={Mail}
              accent="bg-emerald-50 text-emerald-600"
              active={statusFilter === "sent"}
              onClick={() =>
                setStatusFilter(statusFilter === "sent" ? "" : "sent")
              }
            />
            <SummaryCard
              label="Opened"
              value={summary.opened}
              icon={Eye}
              accent="bg-blue-50 text-blue-600"
              active={statusFilter === "opened"}
              onClick={() =>
                setStatusFilter(statusFilter === "opened" ? "" : "opened")
              }
            />
            <SummaryCard
              label="Clicked"
              value={summary.clicked}
              icon={MousePointerClick}
              accent="bg-purple-50 text-purple-600"
              active={statusFilter === "clicked"}
              onClick={() =>
                setStatusFilter(statusFilter === "clicked" ? "" : "clicked")
              }
            />
            <SummaryCard
              label="Bounced"
              value={summary.bounced}
              icon={AlertTriangle}
              accent="bg-red-50 text-red-500"
              active={statusFilter === "bounced"}
              onClick={() =>
                setStatusFilter(statusFilter === "bounced" ? "" : "bounced")
              }
            />
            <SummaryCard
              label="Failed"
              value={summary.failed}
              icon={XCircle}
              accent="bg-red-50 text-red-600"
              active={statusFilter === "failed"}
              onClick={() =>
                setStatusFilter(statusFilter === "failed" ? "" : "failed")
              }
            />
            <SummaryCard
              label="Unsubs"
              value={summary.unsubscribed}
              icon={UserMinus}
              accent="bg-amber-50 text-amber-600"
              active={statusFilter === "unsubscribed"}
              onClick={() =>
                setStatusFilter(
                  statusFilter === "unsubscribed" ? "" : "unsubscribed"
                )
              }
            />
            <SummaryCard
              label="Complaints"
              value={summary.complained}
              icon={ShieldAlert}
              accent="bg-red-50 text-red-700"
              active={statusFilter === "complained"}
              onClick={() =>
                setStatusFilter(
                  statusFilter === "complained" ? "" : "complained"
                )
              }
            />
            <SummaryCard
              label="Pending"
              value={summary.pending}
              icon={Clock}
              accent="bg-amber-50 text-amber-500"
              active={statusFilter === "pending"}
              onClick={() =>
                setStatusFilter(statusFilter === "pending" ? "" : "pending")
              }
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
          <div className="px-5 py-4 border-b border-black/[0.06] flex flex-wrap items-center gap-3">
            {/* Status pills */}
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    statusFilter === f.key
                      ? "bg-ink text-white"
                      : "bg-gray-100 text-slate hover:bg-sand"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex-1" />

            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate"
              />
              <input
                type="text"
                placeholder="Search by email or name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 pr-8 py-2 rounded-xl bg-paper border border-black/[0.08] text-sm w-56 focus:outline-none focus:ring-1 focus:ring-coral/40"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate hover:text-ink"
                >
                  <X size={13} />
                </button>
              )}
            </form>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-slate">
              <Mail size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {search || statusFilter
                  ? "No results match your filters."
                  : "No send data yet for this campaign."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate border-b border-black/[0.06]">
                      <th className="px-5 py-3 font-semibold w-8"></th>
                      <th className="px-5 py-3 font-semibold">Recipient</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Sent</th>
                      <th className="px-5 py-3 font-semibold">Opened</th>
                      <th className="px-5 py-3 font-semibold">Clicked</th>
                      <th className="px-5 py-3 font-semibold">Subject</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const isExpanded =
                        expandedRow === item.subscriber_id;
                      const Icon =
                        STATUS_ICON[item.status] || CheckCircle2;
                      const color =
                        STATUS_COLOR[item.status] || "text-emerald-600";

                      return (
                        <Fragment key={item.subscriber_id}>
                          <tr
                            onClick={() =>
                              setExpandedRow(
                                isExpanded ? null : item.subscriber_id
                              )
                            }
                            className={`border-b border-black/[0.04] cursor-pointer transition hover:bg-paper/60 ${
                              isExpanded ? "bg-paper/40" : ""
                            }`}
                          >
                            <td className="px-5 py-3.5 text-slate">
                              {isExpanded ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              <div>
                                <p className="font-medium text-ink truncate max-w-[220px]">
                                  {item.email}
                                </p>
                                {item.name && (
                                  <p className="text-xs text-slate">
                                    {item.name}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <span
                                className={`inline-flex items-center gap-1.5 text-xs font-semibold capitalize ${color}`}
                              >
                                <Icon size={13} />
                                {item.status}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-slate text-xs">
                              {formatDate(
                                item.sent_at || item.queued_at || null
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              {item.opened ? (
                                <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium">
                                  <Eye size={12} />
                                  {formatDate(item.opened_at)}
                                </span>
                              ) : (
                                <span className="text-xs text-slate/50">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              {item.clicked ? (
                                <span className="inline-flex items-center gap-1 text-xs text-purple-600 font-medium">
                                  <MousePointerClick size={12} />
                                  {formatDate(item.clicked_at)}
                                </span>
                              ) : (
                                <span className="text-xs text-slate/50">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-xs text-slate truncate max-w-[200px]">
                              {item.subject}
                            </td>
                          </tr>
                          {isExpanded && (
                            <ExpandedRow
                              item={item}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-5 py-4 border-t border-black/[0.06] flex items-center justify-between">
                <p className="text-xs text-slate">
                  Showing{" "}
                  <span className="font-medium text-ink">
                    {(page - 1) * limit + 1}–
                    {Math.min(page * limit, total)}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-ink">
                    {total.toLocaleString()}
                  </span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg hover:bg-sand transition disabled:opacity-30"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-ink font-medium tabular-nums">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg hover:bg-sand transition disabled:opacity-30"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
